/**
 * Auth Context
 * Provides authentication state and methods throughout the app
 * Supports multi-tenant architecture with role-based access
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import authService from "../services/authService";
import { ROLES } from "../constants/roles";

const AuthContext = createContext(null);

// API keys - prefer environment variables, fallback to known dev keys
// In production, API key should come from the login response
const getApiKeyForClient = (clientId) => {
  // Check environment variables first (set in .env.local for dev)
  const envKey = import.meta.env[
    `VITE_API_KEY_${clientId?.toUpperCase()?.replace("-", "_")}`
  ];
  if (envKey) return envKey;

  // Development fallbacks - only used if env vars not set
  const devKeys = {
    demo: "srop_demo_d3m0k3y123456789abcdef",
    "ourbus-production":
      "srop_live_ourbus-production_396458e65542ec0b0f5f17123fd59a08",
    "platform-admin": "srop_admin_demo_7c2e25e300da8d191fe15a1e280ae9c6",
    redbus: "srop_redbus_r3dbu5k3y_pr0duct10n",
    acme: "test-api-key-12345",
  };
  return devKeys[clientId];
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check auth status on mount and restore session
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("srop_auth_token");

      if (!token) {
        setIsLoading(false);
        return;
      }

      // Validate token with API
      try {
        const response = await authService.getCurrentUser();
        // Transform API response to user format
        const userData = {
          id: response.user?.id,
          email: response.user?.email,
          name: response.user?.name,
          role: response.user?.role,
          permissions: response.user?.permissions,
          tenant_id: response.tenant?.id,
          tenant_name: response.tenant?.name,
          billing_type: response.tenant?.billing_type,
        };
        setUser(userData);

        // Store API key for the client
        const clientId = response.tenant?.id;
        const apiKey = getApiKeyForClient(clientId);
        if (apiKey) {
          localStorage.setItem("srop_api_key", apiKey);
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        // Token invalid, clear it
        authService.logout();
        localStorage.removeItem("srop_user_email");
        localStorage.removeItem("srop_api_key");
        setUser(null);
      }

      setIsLoading(false);
    };

    // checkAuth();
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.login(email, password);

      // Transform API response to user format
      const userData = {
        id: response.user?.id,
        email: response.user?.email,
        name: response.user?.name,
        role: response.user?.role,
        permissions: response.user?.permissions,
        tenant_id: response.client_id,
        tenant_name: response.user?.name,
      };
      setUser(userData);

      // Store API key for the client (prefer from response, fallback to config)
      if (response.api_key) {
        localStorage.setItem("srop_api_key", response.api_key);
      } else {
        const apiKey = getApiKeyForClient(response.client_id);
        if (apiKey) {
          localStorage.setItem("srop_api_key", apiKey);
        }
      }

      localStorage.setItem("srop_user_email", email);

      return { user: userData, token: response.token };
    } catch (err) {
      console.error("Login error:", err);
      const message =
        err.response?.data?.error?.message || err.message || "Login failed";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Register
  const register = useCallback(async (userData) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.register(userData);
      setUser(response.user);
      return response;
    } catch (err) {
      const message =
        err.response?.data?.error?.message || "Registration failed";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    authService.logout();
    localStorage.removeItem("srop_user_email");
    localStorage.removeItem("srop_api_key"); // Clear admin API key
    setUser(null);
    setError(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user: {
      id: "123456",
      email: "admin@gmail.com",
      role: "admin",
      name: "Admin",
      permissions: true,
      tenant_id: "654987",
      tenant_name: "Admin tenent",
    },
    isLoading,
    // isAuthenticated: !!user,
    isAuthenticated: true,
    error,
    login,
    register,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
